/**
 * Tooltip positioning — the situational half of the .ws-tip primitive.
 *
 * The chip is already usable without this file: CSS parks it above the anchor,
 * centred, which is the right answer nearly everywhere. What CSS cannot do is
 * notice that "above" would land off the top of the window, or that a chip
 * centred on a button near the right edge would hang past it. That is the job
 * here — measure on hover/focus, pick a side that fits, slide the chip along
 * the cross axis until it is inside, and move the caret back over the anchor's
 * centre so it still points at the thing it belongs to.
 *
 * Everything is written back as custom properties with CSS-side defaults
 * (see src/sass/modules/_tooltip.scss), so a failure here degrades to the
 * plain centred-above chip rather than to a broken one.
 */

// Breathing room between the chip and the edge it is being kept inside.
const EDGE_MARGIN = 8;

// Preferred side first, then its opposite, then the two perpendicular ones —
// flipping to the far side keeps the chip on the axis the author asked for,
// which is the least surprising fallback.
const FALLBACKS = {
  top: ['top', 'bottom', 'right', 'left'],
  bottom: ['bottom', 'top', 'right', 'left'],
  left: ['left', 'right', 'top', 'bottom'],
  right: ['right', 'left', 'top', 'bottom'],
};

const HORIZONTAL = { top: true, bottom: true };

let active = null; // { anchor, tip } currently shown
let frame = 0;

/**
 * The box the chip has to stay inside: the viewport, further tightened by any
 * scrolling or clipping ancestor. Without the ancestor walk a chip inside an
 * `overflow: hidden` strip would happily be placed "in the viewport" and then
 * be cut in half by the strip.
 */
function clipBounds(anchor) {
  const bounds = {
    top: EDGE_MARGIN,
    left: EDGE_MARGIN,
    right: document.documentElement.clientWidth - EDGE_MARGIN,
    bottom: document.documentElement.clientHeight - EDGE_MARGIN,
  };

  for (let el = anchor.parentElement; el && el !== document.body; el = el.parentElement) {
    const cs = getComputedStyle(el);
    if (cs.overflow === 'visible' && cs.overflowX === 'visible' && cs.overflowY === 'visible') {
      continue;
    }
    const box = el.getBoundingClientRect();
    bounds.top = Math.max(bounds.top, box.top);
    bounds.left = Math.max(bounds.left, box.left);
    bounds.right = Math.min(bounds.right, box.right);
    bounds.bottom = Math.min(bounds.bottom, box.bottom);
  }

  return bounds;
}

/** How far the chip pokes past the bound on the side it points from; <= 0 fits. */
function overflowOf(rect, bounds, at) {
  if (at === 'top') return bounds.top - rect.top;
  if (at === 'bottom') return rect.bottom - bounds.bottom;
  if (at === 'left') return bounds.left - rect.left;
  return rect.right - bounds.right;
}

/**
 * How far the caret may sit from the chip's edge before it starts crawling
 * onto the rounded corner. Read from the rendered CSS rather than hard-coded,
 * so changing --ws-tip-radius or --ws-tip-caret cannot desynchronise the two.
 *
 * The caret is a border triangle, so the side facing the chip is collapsed to
 * zero width and which side that is depends on the placement. The largest of
 * the four is the one we want, whichever way the chip is currently pointing.
 */
function caretLimits(tip, length) {
  const radius = parseFloat(getComputedStyle(tip).borderTopLeftRadius) || 0;
  const caret = getComputedStyle(tip, '::after');
  const half = Math.max(
    parseFloat(caret.borderTopWidth) || 0,
    parseFloat(caret.borderRightWidth) || 0,
    parseFloat(caret.borderBottomWidth) || 0,
    parseFloat(caret.borderLeftWidth) || 0
  );
  const inset = radius + half;
  // A chip narrower than two corners' worth has nowhere safe to put the caret;
  // dead-centre is the least bad answer.
  if (inset * 2 >= length) return [length / 2, length / 2];
  return [inset, length - inset];
}

function place(anchor, tip) {
  // offsetParent is null while anything up the chain is display: none — there
  // is nothing to measure and nothing to show.
  if (!tip.offsetParent) return;

  // Measure from scratch: a shift left over from the last time this chip was
  // shown would otherwise be baked into every rect below.
  tip.style.removeProperty('--ws-tip-shift');
  tip.style.removeProperty('--ws-tip-caret-pos');

  const preferred = FALLBACKS[tip.dataset.wsTipPlace] ? tip.dataset.wsTipPlace : 'top';
  const bounds = clipBounds(anchor);
  const anchorBox = anchor.getBoundingClientRect();

  // Try each candidate for real rather than predicting it: the chip's size can
  // change between sides (a 20rem max-width wraps differently once the
  // available room changes), so only a measured rect is trustworthy. The
  // preferred side is measured first and breaks out immediately, so the common
  // case costs one layout, not four.
  let best = null;
  for (const candidate of FALLBACKS[preferred]) {
    tip.dataset.wsTipAt = candidate;
    // Safe to read straight back: the chip animates opacity only, so nothing
    // it has is mid-flight and the rect is the position it is really in.
    const rect = tip.getBoundingClientRect();
    const overflow = overflowOf(rect, bounds, candidate);
    if (overflow <= 0) {
      best = { at: candidate, rect };
      break;
    }
    // Nothing fits in a window this small; remember the least bad side rather
    // than letting the loop's last candidate win by accident.
    if (!best || overflow < best.overflow) best = { at: candidate, rect, overflow };
  }

  const { at, rect } = best;
  tip.dataset.wsTipAt = at;

  // Cross-axis nudge: push the chip back inside whichever edge it overshot.
  // Only one of the two can be true unless the chip is wider than the bounds,
  // in which case the leading edge wins and the tail is allowed to overflow —
  // better than centring the overflow on both sides.
  const horizontal = HORIZONTAL[at];
  const startEdge = horizontal ? rect.left : rect.top;
  const endEdge = horizontal ? rect.right : rect.bottom;
  const lowBound = horizontal ? bounds.left : bounds.top;
  const highBound = horizontal ? bounds.right : bounds.bottom;

  let shift = 0;
  if (startEdge < lowBound) shift = lowBound - startEdge;
  else if (endEdge > highBound) shift = highBound - endEdge;

  // Put the caret back over the anchor's centre, expressed from the chip's own
  // leading edge, then keep it clear of the rounded corners.
  const anchorCentre = horizontal
    ? anchorBox.left + anchorBox.width / 2
    : anchorBox.top + anchorBox.height / 2;
  const length = horizontal ? rect.width : rect.height;
  const [min, max] = caretLimits(tip, length);
  const caret = Math.min(Math.max(anchorCentre - (startEdge + shift), min), max);

  tip.style.setProperty('--ws-tip-shift', `${Math.round(shift)}px`);
  tip.style.setProperty('--ws-tip-caret-pos', `${Math.round(caret)}px`);
}

function show(anchor) {
  const tip = anchor.querySelector(':scope > .ws-tip');
  if (!tip) return;
  active = { anchor, tip };
  place(anchor, tip);
}

function hide(anchor) {
  if (active && active.anchor === anchor) active = null;
}

/**
 * A chip that is already open can go stale when the page moves under it — the
 * side that fitted a moment ago may not fit now. Re-measuring is cheap and
 * only ever runs while something is actually shown.
 */
function reposition() {
  if (!active || frame) return;
  frame = requestAnimationFrame(() => {
    frame = 0;
    if (active) place(active.anchor, active.tip);
  });
}

/**
 * Safety net for markup that carries the chip but forgot the anchor class.
 * Without `position: relative` on the anchor the chip would be placed against
 * some far-away ancestor, which looks like a bug in the primitive rather than
 * a typo in the template.
 */
function adoptOrphans(root) {
  root.querySelectorAll('.ws-tip').forEach((tip) => {
    const anchor = tip.parentElement;
    if (anchor) anchor.classList.add('ws-tip-anchor');
  });
}

function init() {
  adoptOrphans(document);

  // pointerover/focusin rather than pointerenter/focus: those do not bubble,
  // and delegation is what keeps Ajax-replaced markup (the event filter) working
  // without re-binding anything.
  document.addEventListener('pointerover', (event) => {
    const anchor = event.target.closest && event.target.closest('.ws-tip-anchor');
    if (!anchor || (active && active.anchor === anchor)) return;
    show(anchor);
  });

  document.addEventListener('pointerout', (event) => {
    const anchor = event.target.closest && event.target.closest('.ws-tip-anchor');
    // relatedTarget inside the same anchor means the pointer only crossed from
    // the icon to the padding — the chip should stay put.
    if (!anchor || (event.relatedTarget && anchor.contains(event.relatedTarget))) return;
    hide(anchor);
  });

  document.addEventListener('focusin', (event) => {
    const anchor = event.target.closest && event.target.closest('.ws-tip-anchor');
    if (anchor) show(anchor);
  });

  document.addEventListener('focusout', (event) => {
    const anchor = event.target.closest && event.target.closest('.ws-tip-anchor');
    if (anchor) hide(anchor);
  });

  addEventListener('scroll', reposition, { passive: true, capture: true });
  addEventListener('resize', reposition, { passive: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
